import { useEffect, useState } from "react";

export type ShellTheme = "day" | "night";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" ? false : window.matchMedia(query).matches
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return matches;
}

export function useShellTheme(): [ShellTheme, () => void] {
  const [theme, setTheme] = useState<ShellTheme>(() => {
    if (typeof window === "undefined") return "day";
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem("manga-reader-theme");
    } catch {
      // Safari can make storage unavailable in restricted browsing contexts.
    }
    if (saved === "day" || saved === "night") return saved;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "night" : "day";
  });

  useEffect(() => {
    document.documentElement.dataset.shellTheme = theme;
    document.documentElement.style.colorScheme = theme === "night" ? "dark" : "light";
    try {
      window.localStorage.setItem("manga-reader-theme", theme);
    } catch {
      // The current session still keeps the selected theme in React state.
    }
  }, [theme]);

  return [theme, () => setTheme((current) => current === "night" ? "day" : "night")];
}
